import {
  LodgingMediaAssetEnum,
  MealPlanKind,
  MediaCategory,
  type NonResizableImage,
  type RoomInfoProducts,
  type ResizableImage,
} from "@b2bportal/lodging-api";
import {
  Amenity,
  type LodgingTravelWalletOffer,
} from "@b2bportal/lodging-api/lib/api";
import { CallState } from "@hopper-b2b/types";
import { createSelector } from "@reduxjs/toolkit";
import { groupBy, uniq } from "lodash-es";
import type { LodgingAppState } from "../../../../reducer";
import {
  getLodgingInListIndex,
  getLodgingShopTrackingProperties,
} from "../../../../util/utils";
import { getLodgings } from "../../../availability/reducer";

export const getCheckInInstructions = (state: LodgingAppState) =>
  state.lodgingShop.checkInInstructions;

export const getShopCallState = (state: LodgingAppState) =>
  state.lodgingShop.shopCallState;

export const getSelectedLodging = (state: LodgingAppState) =>
  state.lodgingShop.selectedLodging;

export const getSelectedLodgingId = (state: LodgingAppState) =>
  state.lodgingShop.selectedLodging?.lodging?.id;

export const getSelectedLodgingName = (state: LodgingAppState) =>
  state.lodgingShop.selectedLodging?.lodging?.name;

export const getRooms = (state: LodgingAppState) => state.lodgingShop.rooms;

export const getRoomsCallState = (state: LodgingAppState) =>
  state.lodgingShop.roomsCallState;

export const isRoomsLoaded = (state: LodgingAppState) =>
  state.lodgingShop.roomsCallState === CallState.Success;

export const getSelectedRoom = (state: LodgingAppState) =>
  state.lodgingShop.selectedRoom;

export const getSelectedRoomId = (state: LodgingAppState) =>
  state.lodgingShop.selectedRoom?.roomInfo?.roomId;

export const getSelectedRoomRateId = (state: LodgingAppState) =>
  state.lodgingShop.selectedRoomRateId;

export const getShopTrackingProperties = (state: LodgingAppState) =>
  state.lodgingShop.shopTrackingProperties;

export const getShopPriceChange = (state: LodgingAppState) =>
  state.lodgingShop.lodgingPriceChange;

export const getSelectedRoomRate = createSelector(
  getSelectedRoom,
  getSelectedRoomRateId,
  (selectedRoom, selectedRateId) => {
    return selectedRoom?.products?.find(
      (p) => p.rateId.value === selectedRateId
    );
  }
);

// Start of Hotel Hero Gallery Selectors ==================>
export const getSelectedLodgingMedia = createSelector(
  getSelectedLodging,
  (selectedLodging) => selectedLodging?.lodging?.media
);

export const getSelectedLodgingMediaImages = createSelector(
  getSelectedLodging,
  (selectedLodging): Array<NonResizableImage | ResizableImage> =>
    selectedLodging?.lodging?.media.filter(
      (mediaAsset) =>
        mediaAsset.LodgingMediaAsset !== LodgingMediaAssetEnum.Video
    ) || []
);

export const getSelectedLodgingMediaCategories = createSelector(
  getSelectedLodgingMediaImages,
  (images) => uniq(images.map((i) => i.category))
);

export const getSelectedLodgingGroupedMedia = createSelector(
  getSelectedLodgingMediaImages,
  (images) => {
    // Handle categories that don't map to MediaCategory
    const mappedMedia = images.map((m) =>
      MediaCategory[m?.category]
        ? m
        : { ...m, category: MediaCategory.Uncategorized }
    );
    return groupBy(mappedMedia, (media) => media.category);
  }
);
// End of Hotel Hero Gallery Selectors ==================>

export const getCheckInPolicies = (state: LodgingAppState) =>
  state.lodgingShop.checkInPolicies;

export const getCurrentStep = (state: LodgingAppState) =>
  state.lodgingShop.currentStep;

export const getHasSelectedRoom = createSelector(
  getSelectedLodging,
  getSelectedRoom,
  getSelectedRoomRateId,
  (lodging, room, rate) => {
    return !!lodging && !!room && !!rate;
  }
);

export const getSelectedLodgingCoordinatesForStaticMap = createSelector(
  getSelectedLodging,
  (lodging) => {
    const coordinates = lodging?.lodging?.location?.coordinates;
    if (coordinates) {
      return `${coordinates.lat},${coordinates.lon}`;
    }
    return null;
  }
);

export const isAdultsOnly = createSelector(getSelectedLodging, (lodging) => {
  const amenities = lodging?.lodging?.amenities || [];
  return amenities.some((amenity) => amenity.amenity === Amenity.AdultsOnly);
});

export const getSelectedLodgingAmenities = createSelector(
  getSelectedLodging,
  (lodging) => lodging?.lodging?.amenities
);

export const getHotelHasPaidBreakfast = createSelector(
  getSelectedLodgingAmenities,
  (amenities) =>
    amenities?.some((a) => a.amenity === Amenity.PaidBreakfast) ?? false
);

export const getSelectedLodgingOfferOpaque = (state: LodgingAppState) =>
  state.lodgingShop.selectedOfferOpaque;

export const getSelectedLodgingInListIndex = createSelector(
  getSelectedLodgingId,
  getLodgings,
  getLodgingInListIndex
);

export const getHotelShopTrackingProperties = createSelector(
  getSelectedLodging,
  getSelectedLodgingInListIndex,
  getLodgingShopTrackingProperties
);

export const getSelectedRoomTrackingProperties = createSelector(
  getSelectedRoom,
  (selectedRoom) => selectedRoom?.trackingPropertiesV2
);

export const getSelectedRoomRateTrackingProperties = createSelector(
  getSelectedRoomRate,
  (selectedRoomRate) => selectedRoomRate?.trackingPropertiesV2
);

export const getBestOfferOverall = (
  state: LodgingAppState
): LodgingTravelWalletOffer | undefined => {
  return state.lodgingShop?.selectedLodging?.bestOffer;
};

const getHotelLevelMealPlanKind = (
  rooms: RoomInfoProducts[]
): MealPlanKind | undefined => {
  if (rooms.length === 0) return undefined;

  const allMealPlans = rooms.flatMap((room) =>
    room.products.map((p) => p.mealPlan?.kind ?? MealPlanKind.RoomOnly)
  );

  if (allMealPlans.length === 0) return undefined;

  const uniquePlans = new Set(allMealPlans);
  if (uniquePlans.size !== 1) return undefined;

  const plan = allMealPlans[0];
  return plan !== MealPlanKind.RoomOnly ? plan : undefined;
};

const BREAKFAST_AMENITIES: ReadonlySet<string> = new Set([
  Amenity.FreeBreakfast,
  Amenity.BreakfastIncluded,
]);

const BREAKFAST_MEAL_PLAN_KINDS: ReadonlySet<string> = new Set([
  MealPlanKind.Breakfast,
  MealPlanKind.BreakfastFor1,
  MealPlanKind.BreakfastFor2,
]);

const allHotelRoomsHaveFreeBreakfast = (rooms: RoomInfoProducts[]): boolean => {
  if (rooms.length === 0) return false;

  return rooms.every((room) => {
    if (
      room.roomInfo?.amenities?.some((a) => BREAKFAST_AMENITIES.has(a.amenity))
    ) {
      return true;
    }
    if (room.products.length === 0) return false;
    return room.products.every(
      (p) =>
        p.amenities?.some((a) => BREAKFAST_AMENITIES.has(a.amenity)) ||
        (p.mealPlan?.kind !== undefined &&
          BREAKFAST_MEAL_PLAN_KINDS.has(p.mealPlan.kind))
    );
  });
};

export const getHotelMealPlanKind = createSelector(getRooms, (rooms) =>
  getHotelLevelMealPlanKind(rooms)
);

export const getAllRoomsHaveFreeBreakfast = createSelector(getRooms, (rooms) =>
  allHotelRoomsHaveFreeBreakfast(rooms)
);
